import React, { useCallback, useMemo, useState } from 'react';
import { Collapse, CollapseProps, Input, Select, Table } from 'antd';
import { LinkComponent } from '../../LinkComponent';
import cx from 'classnames';
import dayjs from 'dayjs';
import { lightText } from '../../../instruments';
import Styles from '../../styles/m_viewStyles.less';
import LocalStyles from './m_styles.less';

const { Search } = Input;
const { Panel } = Collapse;

const formatDate = (dateString: string | null) => (dateString ? new Date(dateString).toLocaleDateString() : '∞');

export const CardsList: React.FC<{ assigned_cards: any; card: any }> = ({ assigned_cards, card }) => {
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const cardHierarchyLink = useCallback((cardId: number) => {
        return `/support-page/card-set-hierarchy?cardId=${cardId}`;
    }, []);

    const isStartDateInTheFuture = (startDate: string) => {
        return dayjs(startDate).isAfter(dayjs(), 'day');
    };

    const searchedCards = useMemo(() => {
        if (searchQuery === '') {
            return assigned_cards;
        } else {
            return assigned_cards.filter((assignment: any) => {
                const query = searchQuery.toLowerCase();
                return (
                    assignment.card?.name?.toLowerCase().includes(query) ||
                    assignment.card?.id?.toString().includes(query) ||
                    assignment.card?.eid?.toLowerCase().includes(query) ||
                    assignment.card?.imei?.toLowerCase().includes(query) ||
                    assignment.start_date?.toLowerCase().includes(query) ||
                    assignment.end_date?.toLowerCase().includes(query)
                );
            });
        }
    }, [searchQuery]);

    const statusFiltered = useMemo(() => {
        if (statusFilter === 'future') {
            return searchedCards.filter((card: any) => isStartDateInTheFuture(card.start_date));
        } else if (statusFilter === 'active') {
            return searchedCards.filter((card: any) => card.active);
        } else if (statusFilter === 'inactive') {
            return searchedCards.filter((card: any) => !card.active);
        } else {
            return searchedCards;
        }
    }, [statusFilter, searchedCards]);

    const onStatusFilterChange = (value: any) => {
        setStatusFilter(value);
    };

    const tableColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
        },
        {
            title: 'Card name',
            dataIndex: 'cardName',
        },
        {
            title: 'IMEI',
            dataIndex: 'imei',
        },
        {
            title: 'EID',
            dataIndex: 'eid',
        },
        {
            title: 'Date Range',
            dataIndex: 'dateRange',
        },
        {
            title: 'Status',
            dataIndex: 'status',
        },
        {
            title: 'Hierarchy',
            dataIndex: 'hierarchy',
        },
    ];

    const tableData = statusFiltered.map((log: any) => {
        const {
            active,
            id,
            card: { name, imei, eid, id: cardId },
            start_date,
            end_date,
        } = log;

        return {
            key: id,
            id: lightText(id.toString(), searchQuery),
            cardId,
            cardName: (
                <LinkComponent
                    currentListComponent={'CardList'}
                    id={card?.id}
                    name={name}
                    isLight
                    localSearch={searchQuery}
                />
            ),
            imei: lightText(imei, searchQuery),
            eid: lightText(eid, searchQuery),
            dateRange: (
                <>
                    {lightText(formatDate(start_date), searchQuery)} - {lightText(formatDate(end_date), searchQuery)}
                </>
            ),
            status: isStartDateInTheFuture(start_date) ? (
                <span style={{ color: 'purple' }}>Future</span>
            ) : active ? (
                <span style={{ color: 'green' }}>Active</span>
            ) : (
                <span style={{ color: 'red' }}>Inactive</span>
            ),
            hierarchy: <>{id.toString() !== card?.id?.toString() && <a href={cardHierarchyLink(id)}>Hierarchy</a>}</>,
        };
    });

    return (
        <div>
            <Collapse style={{ width: '100%' }} defaultActiveKey={['1']}>
                <Panel key={'1'} header={'Cards Table'} forceRender={true} style={{ width: '100%' }}>
                    <div className={cx(Styles.tablePlace)}>
                        <div className={LocalStyles.searchContainer}>
                            <Search
                                style={{ marginBottom: 8 }}
                                placeholder="Search card"
                                className={cx(LocalStyles.searchInput, 'input-search')}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                allowClear
                            />
                            <Select
                                defaultValue={'all'}
                                className={LocalStyles.statusDropDown}
                                onChange={onStatusFilterChange}
                                options={[
                                    { value: 'all', label: 'All' },
                                    { value: 'active', label: 'Active' },
                                    { value: 'inactive', label: 'Inactive' },
                                    { value: 'future', label: 'Future' },
                                ]}
                            />
                        </div>
                        <Table
                            className={'table-theme'}
                            columns={tableColumns as any}
                            dataSource={tableData}
                            pagination={false}
                            size={'middle'}
                            rowClassName={(record) =>
                                record.cardId.toString() === card?.id?.toString() ? 'highlighted-row' : ''
                            }
                        />
                    </div>
                </Panel>
            </Collapse>
        </div>
    );
};
